import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import useRemittanceSocket from "../hooks/useRemittanceSocket";
import useChatSocket from "../hooks/useChatSocket";
import { messagePreviewText } from "../services/chatApi";
import { playNotificationSound } from "../Utils/sound";

const LiveAlertsContext = createContext({
  isRemittanceConnected: false,
  isChatConnected: false,
  subscribeRemittanceCreated: () => () => {},
  subscribeRemittanceStatusUpdated: () => () => {},
  subscribeChatUpdated: () => () => {},
});

const TOAST_OPTS = {
  position: "top-right",
  autoClose: 20000,
};

const getContactLabel = (contact) => {
  if (!contact) return null;
  const name = [contact.name, contact.lastName].filter(Boolean).join(" ");
  return name || (contact.id != null ? `#${contact.id}` : null);
};

const getRemittanceAmountLabel = (remittance) => {
  if (!remittance) return "";
  const qty = remittance.quantity ?? remittance.amount;
  const code = remittance.currency?.code || remittance.currency?.name || "";
  if (qty == null || qty === "") return code ? String(code) : "";
  return code ? `${qty} ${code}` : String(qty);
};

const createSubscriber = (listenersRef) => (handler) => {
  if (typeof handler !== "function") return () => {};
  listenersRef.current.add(handler);
  return () => {
    listenersRef.current.delete(handler);
  };
};

const notifyListeners = (listenersRef, payload) => {
  listenersRef.current.forEach((handler) => {
    try {
      handler(payload);
    } catch (err) {
      console.warn("[LiveAlerts] subscriber error:", err);
    }
  });
};

export const LiveAlertsProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const remittanceCreatedListeners = useRef(new Set());
  const remittanceStatusListeners = useRef(new Set());
  const chatUpdatedListeners = useRef(new Set());

  const activeChatKey = useMemo(() => {
    const match = location.pathname.match(
      /^\/exchange-dashboard\/chats\/([^/]+)/
    );
    return match ? match[1] : null;
  }, [location.pathname]);
  const activeChatKeyRef = useRef(activeChatKey);
  useEffect(() => {
    activeChatKeyRef.current = activeChatKey;
  }, [activeChatKey]);

  const handleRemittanceCreated = useCallback(
    (remittance) => {
      notifyListeners(remittanceCreatedListeners, remittance);
      if (!remittance?.id) return;

      const contact = getContactLabel(remittance.contact);
      const amount = getRemittanceAmountLabel(remittance);
      const parts = [
        `New remittance #${remittance.id}`,
        contact ? `from ${contact}` : null,
        amount || null,
      ].filter(Boolean);

      toast.success(parts.join(" — "), {
        ...TOAST_OPTS,
        toastId: `remittance-created-${remittance.id}`,
        onClick: () => navigate("/exchange-dashboard"),
      });
      playNotificationSound();
    },
    [navigate]
  );

  const handleRemittanceStatusUpdated = useCallback(
    (remittance) => {
      notifyListeners(remittanceStatusListeners, remittance);
      if (!remittance?.id) return;

      const status = remittance.status || "updated";
      const contact = getContactLabel(remittance.contact);
      const parts = [
        `Remittance #${remittance.id}`,
        `→ ${status}`,
        contact ? `(${contact})` : null,
      ].filter(Boolean);

      toast.info(parts.join(" "), {
        ...TOAST_OPTS,
        toastId: `remittance-status-${remittance.id}-${status}`,
        onClick: () => navigate("/exchange-dashboard"),
      });
      playNotificationSound();
    },
    [navigate]
  );

  const handleChatSocketEvent = useCallback(
    (payload, { showToast = true } = {}) => {
      notifyListeners(chatUpdatedListeners, payload);
      if (!showToast || !payload?.conversationId) return;

      const senderType = payload?.message?.senderType;
      // Ignore admin's own outbound echoes for toast alerts
      if (senderType === "admin") return;

      const chatKey = String(payload.conversationId);
      // Already viewing this thread — UI updates elsewhere; skip toast noise
      if (activeChatKeyRef.current === chatKey) return;

      const messageKey =
        payload.message?.id ??
        payload.message?.createdAt ??
        payload.lastMessageAt ??
        Date.now();
      const toastId = `chat-${payload.conversationId}-${messageKey}`;

      // Same message can arrive via conversation_updated + unread_count_update
      if (toast.isActive(toastId)) return;

      const contact = getContactLabel(payload.contact);
      const preview = messagePreviewText(payload.message);
      const title = contact
        ? `New chat message from ${contact}`
        : "New chat message";
      const body = preview ? `${title}: ${preview}` : title;

      toast.info(body, {
        ...TOAST_OPTS,
        toastId,
        onClick: () =>
          navigate(`/exchange-dashboard/chats/${payload.conversationId}`),
      });
      playNotificationSound();
    },
    [navigate]
  );

  const handleConversationUpdated = useCallback(
    (payload) => handleChatSocketEvent(payload, { showToast: true }),
    [handleChatSocketEvent]
  );

  const handleUnreadUpdate = useCallback(
    (payload) => handleChatSocketEvent(payload, { showToast: false }),
    [handleChatSocketEvent]
  );

  const { isConnected: isRemittanceConnected } = useRemittanceSocket({
    onCreated: handleRemittanceCreated,
    onStatusUpdated: handleRemittanceStatusUpdated,
  });

  const { isConnected: isChatConnected } = useChatSocket(
    {
      onConversationUpdated: handleConversationUpdated,
      onUnreadUpdate: handleUnreadUpdate,
    },
    null,
    true
  );

  const subscribeRemittanceCreated = useMemo(
    () => createSubscriber(remittanceCreatedListeners),
    []
  );
  const subscribeRemittanceStatusUpdated = useMemo(
    () => createSubscriber(remittanceStatusListeners),
    []
  );
  const subscribeChatUpdated = useMemo(
    () => createSubscriber(chatUpdatedListeners),
    []
  );

  const value = useMemo(
    () => ({
      isRemittanceConnected,
      isChatConnected,
      subscribeRemittanceCreated,
      subscribeRemittanceStatusUpdated,
      subscribeChatUpdated,
    }),
    [
      isRemittanceConnected,
      isChatConnected,
      subscribeRemittanceCreated,
      subscribeRemittanceStatusUpdated,
      subscribeChatUpdated,
    ]
  );

  return (
    <LiveAlertsContext.Provider value={value}>
      {children}
    </LiveAlertsContext.Provider>
  );
};

export const useLiveAlerts = () => useContext(LiveAlertsContext);

LiveAlertsProvider.propTypes = {
  children: PropTypes.node,
};

export default LiveAlertsContext;
